import React, { useState, useMemo } from 'react';

import { Copy } from 'lucide-react';
import { toast } from 'react-toastify';

import BaseList from '../../ui-components/BaseList';

import LogDetailsModal from './LogDetailsModal';
import { useGetLogsQuery } from './logsApi';

import type { LogRecord } from './model';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatTimestampToDateTime } from '../../utils/common';

const CorrelationIdRenderer: React.FC<ICellRendererParams<LogRecord>> = ({
  value,
}) => {
  return (
    <div className="flex items-center space-x-2">
      <span className="font-mono text-xs">{String(value)?.slice(0, 8)}</span>
      <button
        onClick={() => {
          navigator.clipboard
            .writeText(String(value))
            .then(() => toast.success('Id copied to clipboard successfully'))
            .catch(() => toast.error('Failed to copy ID'));
        }}
        className="bg-brand-500 cursor-pointer text-white text-xs px-2 py-1 rounded hover:bg-brand-600"
        type="button"
      >
        <Copy size={15} />
      </button>
    </div>
  );
};

CorrelationIdRenderer.displayName = 'CorrelationIdRenderer';

const createLevelRenderer = (
  onOpenModal: (log: LogRecord) => void
): React.FC<ICellRendererParams<LogRecord>> => {
  const LevelRenderer: React.FC<ICellRendererParams<LogRecord>> = (params) => (
    <button
      className="text-brand-600 hover:underline hover:text-brand-800 cursor-pointer transition font-semibold"
      onClick={() => onOpenModal(params.data as LogRecord)}
      type="button"
    >
      {String(params.value)}
    </button>
  );

  LevelRenderer.displayName = 'LevelRenderer';
  return LevelRenderer;
};

const LogsPage: React.FC = () => {
  const [selectedLog, setSelectedLog] = useState<LogRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [payload, setPayload] = useState({
    page: 1,
    limit: 10,
    sort: [],
    filters: [],
    keyword: '',
    addedDate: { start: '', end: '' },
  });

  const { data, isFetching, refetch } = useGetLogsQuery(payload);

  const fetchData = useMemo(() => {
    return () => ({ data, isFetching, refetch });
  }, [data, isFetching, refetch]);

  const LevelRenderer = useMemo(
    () =>
      createLevelRenderer((log: LogRecord) => {
        setSelectedLog(log);
        setModalOpen(true);
      }),
    []
  );

  const columns: ColDef<LogRecord>[] = [
    {
      headerName: 'Id',
      field: 'correlation Id',
      minWidth: 150,
      sortable: false,
      cellRenderer: CorrelationIdRenderer,
    },
    {
      headerName: 'User name',
      field: 'userName',
      minWidth: 80,
      sortable: false,
    },
    { headerName: 'Log type', field: 'type', minWidth: 80, sortable: false },
    {
      headerName: 'Log level',
      field: 'level',
      minWidth: 80,
      sortable: false,
      cellRenderer: LevelRenderer,
    },
    {
      headerName: 'Log message',
      field: 'message',
      minWidth: 200,
      sortable: false,
    },
    {
      headerName: 'Browser info',
      field: 'systemInfo',
      minWidth: 100,
      sortable: false,
      valueGetter: (params) =>
        params.data?.systemInfo?.details?.browser?.name || '-',
    },
    {
      headerName: 'OS name',
      field: 'systemInfo',
      minWidth: 100,
      sortable: false,
      valueGetter: (params) =>
        params.data?.systemInfo?.details?.os?.name || '-',
    },
    {
      headerName: 'OS version',
      field: 'systemInfo',
      minWidth: 100,
      sortable: false,
      valueGetter: (params) =>
        params.data?.systemInfo?.details?.os?.version || '-',
    },
    {
      headerName: 'Date created',
      field: 'addedDate',
      minWidth: 160,
      valueGetter: (params) => {
        const value = params.data?.addedDate;
        return value ? formatTimestampToDateTime(value) : '-';
      },
    },
  ];

  return (
    <>
      <BaseList<LogRecord>
        title={{ singular: 'Log', plural: 'Logs' }}
        columns={columns}
        fetchData={fetchData}
        searchBy="Message and Correl..."
        enableCheckbox={false}
        extractRowIds={(records) => records.map((r) => r.logId)}
        onPayloadChange={(p) => setPayload(p as any)}
        headerMargin="mt-[-30px]"
      />
      <LogDetailsModal
        log={selectedLog}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default LogsPage;
